import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  ListItemSecondaryAction,
  alpha,
} from '@mui/material';
import {
  Send as SendIcon,
  Menu as MenuIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ChevronLeft as ChevronLeftIcon,
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  VideoFile as VideoIcon,
  AudioFile as AudioIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { Chip } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';

interface FileAttachment {
  name: string;
  size: number;
  type: string;
  dataUrl: string;
  inputType: string;
}

interface Message {
  text: string;
  isUser: boolean;
  timestamp: string;
  credits?: number;
  attachments?: FileAttachment[];
}

interface ChatThread {
  id: string;
  title: string;
  lastMessage: string;
  timestamp: string;
  messages: Message[];
  totalCredits: number;
}

interface ChatbotConfig {
  name: string;
  description: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  layoutStyle: 'modern' | 'classic' | 'minimal';
  logoUrl?: string;
  bannerUrl?: string;
}

export const ChatbotEmbed: React.FC = () => {
  const [searchParams] = useSearchParams();
  const chatbotId = searchParams.get('chatbotId');
  const apiKey = searchParams.get('apiKey');
  const customerTag = searchParams.get('customerTag');
  
  const [chatbot, setChatbot] = useState<any>(null);
  const [branding, setBranding] = useState<ChatbotConfig>({
    name: 'AI Assistant',
    description: 'How can I help you today?',
    primaryColor: '#e0e0e0',
    secondaryColor: '#bdbdbd',
    accentColor: '#9e9e9e',
    layoutStyle: 'modern',
  });
  
  const [threads, setThreads] = useState<ChatThread[]>([]);
  const [currentThreadId, setCurrentThreadId] = useState<string>('');
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [attachedFiles, setAttachedFiles] = useState<FileAttachment[]>([]);
  const [maxThreadLength, setMaxThreadLength] = useState(25);
  const [allowAttachments, setAllowAttachments] = useState(true);
  const [knowledgeCategories, setKnowledgeCategories] = useState<string[]>([]);
  const [knowledgeChunks, setKnowledgeChunks] = useState(2);
  const [introMessage, setIntroMessage] = useState('');
  const [overrideTitle, setOverrideTitle] = useState('');
  const [showThreadDrawer, setShowThreadDrawer] = useState(false);
  const [showCreditCounter, setShowCreditCounter] = useState(true);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (chatbotId && apiKey) {
      loadChatbot();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatbotId, apiKey]);

  const currentThreadMessages = getCurrentThread()?.messages;
  useEffect(() => {
    scrollToBottom();
  }, [currentThreadMessages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const generateUUID = (): string => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : ((r & 0x3) | 0x8);
      return v.toString(16);
    });
  };

  function getCurrentThread(): ChatThread | undefined {
    return threads.find(t => t.id === currentThreadId);
  }

  const loadThreadsFromStorage = () => {
    const storageKey = `chatbot_embed_threads_${chatbotId}`;
    const savedThreads = localStorage.getItem(storageKey);
    if (savedThreads) {
      const parsedThreads = JSON.parse(savedThreads);
      const normalizedThreads = Array.isArray(parsedThreads)
        ? parsedThreads.map((t: ChatThread) => ({
            ...t,
            id: t.id || generateUUID(),
            messages: Array.isArray(t.messages) ? t.messages : [],
            totalCredits: Number.isFinite(t.totalCredits) ? t.totalCredits : 0
          }))
        : [];

      setThreads(normalizedThreads);
      if (normalizedThreads.length > 0) {
        setCurrentThreadId(normalizedThreads[0].id);
        saveThreadsToStorage(normalizedThreads);
      }
    } else {
      startNewConversation();
    }
  };

  const saveThreadsToStorage = (threadsToSave: ChatThread[]) => {
    const storageKey = `chatbot_embed_threads_${chatbotId}`;
    localStorage.setItem(storageKey, JSON.stringify(threadsToSave));
  };

  const startNewConversation = () => {
    const newThreadId = generateUUID();
    const newThread: ChatThread = {
      id: newThreadId,
      title: 'New conversation',
      lastMessage: '',
      timestamp: new Date().toISOString(),
      messages: introMessage ? [{
        text: introMessage,
        isUser: false,
        timestamp: new Date().toISOString()
      }] : [],
      totalCredits: 0
    };
    
    setThreads(prev => {
      const updatedThreads = [newThread, ...prev];
      saveThreadsToStorage(updatedThreads);
      return updatedThreads;
    });
    setCurrentThreadId(newThreadId);
  };

  const selectThread = (threadId: string) => {
    setCurrentThreadId(threadId);
    setShowThreadDrawer(false);
  };

  const deleteThread = (threadId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const updatedThreads = threads.filter(t => t.id !== threadId);
    setThreads(updatedThreads);
    saveThreadsToStorage(updatedThreads);
    
    if (currentThreadId === threadId) {
      if (updatedThreads.length > 0) {
        setCurrentThreadId(updatedThreads[0].id);
      } else {
        startNewConversation();
      }
    }
  };


  const loadChatbot = async () => {
    try {
      const response = await fetch(`/api/v2/ai-functions/${chatbotId}`, {
        headers: { 'Authorization': `Bearer ${apiKey}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const func = data.data || data;
        setChatbot(func);
        
        // Load branding from metadata
        if (func.metadata?.branding) {
          console.log('[ChatbotEmbed] Loading branding from metadata:', func.metadata.branding);
          setBranding({
            name: func.metadata.branding.name || func.display_name || func.name,
            description: func.metadata.branding.description || func.description || 'How can I help you today?',
            primaryColor: func.metadata.branding.primaryColor || '#9c27b0',
            secondaryColor: func.metadata.branding.secondaryColor || '#7b1fa2',
            accentColor: func.metadata.branding.accentColor || '#e91e63',
            layoutStyle: func.metadata.branding.layoutStyle || 'modern',
            logoUrl: func.metadata.branding.logoUrl || '',
            bannerUrl: func.metadata.branding.bannerUrl || ''
          });
        }
        
        // Load settings from metadata
        if (func.metadata?.chat_history_length) {
          setMaxThreadLength(func.metadata.chat_history_length);
        }
        if (func.metadata?.knowledge_chunks) {
          setKnowledgeChunks(Math.min(func.metadata.knowledge_chunks, 10));
        } else {
          setKnowledgeChunks(2);
        }
        if (func.metadata?.intro_message) {
          setIntroMessage(func.metadata.intro_message);
        }
        if (func.metadata?.override_title) {
          setOverrideTitle(func.metadata.override_title);
        }
        if (func.metadata?.knowledge_categories) {
          setKnowledgeCategories(func.metadata.knowledge_categories);
        }
        if (func.metadata?.allow_attachments !== undefined) {
          setAllowAttachments(func.metadata.allow_attachments);
        } else {
          setAllowAttachments(true); // Default to true
        }
        if (func.metadata?.show_credit_counter !== undefined) {
          setShowCreditCounter(func.metadata.show_credit_counter);
        } else {
          setShowCreditCounter(true); // Default to true
        }
        
        // Load thread history
        loadThreadsFromStorage();
      }
    } catch (error) {
      console.error('Failed to load chatbot:', error);
    } finally {
      setLoading(false);
    }
  };

  // File handling constants and functions
  const MAX_FILE_SIZE_MB = 20;
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
  const MAX_FILES = 3;

  const detectInputType = (file: File): string => {
    const type = file.type.toLowerCase();
    const name = file.name.toLowerCase();
    
    if (type.startsWith('image/')) return 'image';
    if (type.startsWith('video/')) return 'video';
    if (type.startsWith('audio/') || type === 'audio/mpeg' || name.endsWith('.mp3')) return 'audio';
    if (type === 'application/pdf' || name.endsWith('.pdf')) return 'pdf';
    if (type === 'text/csv' || name.endsWith('.csv')) return 'csv';
    if (type === 'text/html' || name.endsWith('.html')) return 'html';
    if (type === 'text/markdown' || name.endsWith('.md')) return 'markdown';
    if (type.startsWith('text/')) return 'text';
    
    return 'binary';
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const newAttachments: FileAttachment[] = [];
    
    for (let i = 0; i < Math.min(files.length, MAX_FILES - attachedFiles.length); i++) {
      const file = files[i];
      
      if (file.size > MAX_FILE_SIZE_BYTES) {
        alert(`File "${file.name}" is too large. Maximum size is ${MAX_FILE_SIZE_MB}MB.`);
        continue;
      }

      try {
        const reader = new FileReader();
        const dataUrl = await new Promise<string>((resolve, reject) => {
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        const inputType = detectInputType(file);
        
        newAttachments.push({
          name: file.name,
          size: file.size,
          type: file.type,
          dataUrl,
          inputType
        });
      } catch (error) {
        console.error(`Error reading file ${file.name}:`, error);
        alert(`Failed to read file "${file.name}"`);
      }
    }

    if (newAttachments.length > 0) {
      setAttachedFiles(prev => [...prev, ...newAttachments]);
    }

    event.target.value = '';
  };

  const removeAttachedFile = (index: number) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const sendMessage = async () => {
    const text = input.trim();
    if ((!text && attachedFiles.length === 0) || sending || !chatbot) return;

    const existingThread = threads.find(t => t.id === currentThreadId);
    let resolvedThreadId = currentThreadId;

    if (!resolvedThreadId || !existingThread) {
      resolvedThreadId = generateUUID();
      const fallbackThread: ChatThread = {
        id: resolvedThreadId,
        title: overrideTitle || branding.name || chatbot?.display_name || chatbot?.name || 'New conversation',
        lastMessage: '',
        timestamp: new Date().toISOString(),
        messages: introMessage ? [{
          text: introMessage,
          isUser: false,
          timestamp: new Date().toISOString()
        }] : [],
        totalCredits: 0
      };
      setThreads(prev => {
        const updatedThreads = [fallbackThread, ...prev];
        saveThreadsToStorage(updatedThreads);
        return updatedThreads;
      });
      setCurrentThreadId(resolvedThreadId);
    }

    const currentThread: ChatThread = existingThread ?? {
      id: resolvedThreadId,
      title: overrideTitle || branding.name || chatbot?.display_name || chatbot?.name || 'New conversation',
      lastMessage: '',
      timestamp: new Date().toISOString(),
      messages: introMessage ? [{
        text: introMessage,
        isUser: false,
        timestamp: new Date().toISOString()
      }] : [],
      totalCredits: 0
    };

    const newMessage: Message = { 
      text, 
      isUser: true, 
      timestamp: new Date().toISOString(),
      attachments: attachedFiles.length > 0 ? [...attachedFiles] : undefined
    };
    
    // Check if this is the first user message
    const isFirstUserMessage = !currentThread.messages.find(m => m.isUser);
    
    // Update thread with new message and title if first message
    setThreads(prev => {
      const updatedThreads = prev.map(t => {
        if (t.id !== resolvedThreadId) return t;
        const updatedThread = {
          ...t,
          messages: [...t.messages, newMessage],
          timestamp: new Date().toISOString()
        };
        if (isFirstUserMessage) {
          const title = text || (attachedFiles.length > 0 ? `[File: ${attachedFiles[0].name}]` : 'New conversation');
          updatedThread.title = title.slice(0, 30) + (title.length > 30 ? '...' : '');
        }
        return updatedThread;
      });
      saveThreadsToStorage(updatedThreads);
      return updatedThreads;
    });
    setInput('');
    const currentAttachments = [...attachedFiles];
    setAttachedFiles([]);
    setSending(true);

    try {
      let requestBody: any;
      
      if (currentAttachments.length > 0) {
        // If we have files, send the first file as input and text as message
        requestBody = {
          input: currentAttachments[0].dataUrl,
          input_type: currentAttachments[0].inputType,
          message: text || undefined,
          metadata: {
            filename: currentAttachments[0].name,
            file_size: currentAttachments[0].size,
            content_type: currentAttachments[0].type
          }
        };
        
        // Add additional files
        if (currentAttachments.length > 1) {
          currentAttachments.slice(1).forEach((file, idx) => {
            (requestBody as any)[`input${idx + 1}`] = file.dataUrl;
          });
        }
      } else {
        // Text only
        requestBody = {
          input: text,
          input_type: 'text'
        };
      }
      
      // Add vector search if knowledge categories are set
      const resolvedKnowledgeCategories = Array.from(new Set(knowledgeCategories.filter(Boolean)));
      if (resolvedKnowledgeCategories.length > 0) {
        if (!requestBody.metadata) requestBody.metadata = {};
        requestBody.metadata.vector_search = {
          enabled: true,
          category_ids: resolvedKnowledgeCategories,
          top_k: Math.max(1, Math.min(knowledgeChunks || 2, 50))
        };
      }
      
      const headers: any = {
        'Authorization': `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
        'EASYAI-THREAD-ID': resolvedThreadId,
        'EASYAI-MAX-THREAD-LEN': maxThreadLength.toString()
      };

      const response = await fetch(`/api/v2/functions/${customerTag}/${chatbot.name}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const data = await response.json();
        const botResponse = data.result || data.response || 'No response received';
        const headerCredits = response.headers?.get('easyai-request-credits-used');
        const creditsUsed = headerCredits ? parseFloat(headerCredits) || 0 : data.usage?.credits || 0;
        
        const botMessage: Message = {
          text: botResponse,
          isUser: false,
          timestamp: new Date().toISOString(),
          credits: creditsUsed
        };
        
        // Update thread with bot response and credits
        setThreads(prev => {
          const finalThreads = prev.map(t =>
            t.id === resolvedThreadId
              ? {
                  ...t,
                  messages: [...t.messages, botMessage],
                  lastMessage: botResponse,
                  timestamp: new Date().toISOString(),
                  totalCredits: t.totalCredits + creditsUsed
                }
              : t
          );
          saveThreadsToStorage(finalThreads);
          return finalThreads;
        });
      } else {
        const errorMessage: Message = {
          text: 'Sorry, I encountered an error. Please try again.',
          isUser: false,
          timestamp: new Date().toISOString()
        };

        setThreads(prev => {
          const errorThreads = prev.map(t =>
            t.id === resolvedThreadId
              ? {
                  ...t,
                  messages: [...t.messages, errorMessage],
                  lastMessage: errorMessage.text,
                  timestamp: new Date().toISOString()
                }
              : t
          );
          saveThreadsToStorage(errorThreads);
          return errorThreads;
        });
      }
    } catch (error) {
      console.error('API Error:', error);
      const errorMessage: Message = {
        text: 'Sorry, I encountered an error. Please try again.',
        isUser: false,
        timestamp: new Date().toISOString()
      };

      setThreads(prev => {
        const errorThreads = prev.map(t =>
          t.id === resolvedThreadId
            ? {
                ...t,
                messages: [...t.messages, errorMessage],
                lastMessage: errorMessage.text,
                timestamp: new Date().toISOString()
              }
            : t
        );
        saveThreadsToStorage(errorThreads);
        return errorThreads;
      });
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: '#fafafa',
      }}>
        <CircularProgress sx={{ color: '#bdbdbd' }} />
      </Box>
    );
  }

  if (!chatbot) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        bgcolor: '#f5f5f5'
      }}>
        <Typography variant="h6" color="error">
          Invalid chatbot configuration
        </Typography>
      </Box>
    );
  }

  const getHeaderStyle = () => {
    // Banner overrides all other styles
    if (branding.bannerUrl) {
      return {
        background: `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.3)), url(${branding.bannerUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        color: 'white',
        p: branding.layoutStyle === 'classic' ? 2.5 : 2,
        display: 'flex',
        alignItems: 'center',
        gap: 2
      };
    }
    
    switch (branding.layoutStyle) {
      case 'classic':
        return {
          background: `linear-gradient(135deg, ${branding.primaryColor} 0%, ${branding.secondaryColor} 100%)`,
          color: 'white',
          p: 2.5,
          textAlign: 'center' as const,
          position: 'relative' as const
        };
      case 'minimal':
        return {
          bgcolor: 'white',
          color: 'text.primary',
          p: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5
        };
      default: // modern
        return {
          background: `linear-gradient(135deg, ${branding.primaryColor} 0%, ${branding.secondaryColor} 100%)`,
          color: 'white',
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        };
    }
  };

  const getLogoStyle = () => {
    const baseStyle = {
      backgroundImage: `url(${branding.logoUrl})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    };
    
    // Don't show logo if we have a banner
    if (branding.bannerUrl) return { display: 'none' };
    
    switch (branding.layoutStyle) {
      case 'classic':
        return {
          ...baseStyle,
          width: 50,
          height: 50,
          borderRadius: '50%',
          margin: '0 auto 8px',
          border: '3px solid white'
        };
      case 'minimal':
        return {
          ...baseStyle,
          width: 30,
          height: 30,
          borderRadius: 1,
        };
      default: // modern
        return {
          ...baseStyle,
          width: 40,
          height: 40,
          borderRadius: '50%',
          border: '2px solid white',
          flexShrink: 0
        };
    }
  };

  const getDrawerStyle = () => {
    switch (branding.layoutStyle) {
      case 'classic':
        return {
          bgcolor: alpha(branding.primaryColor, 0.05),
          '& .MuiListItemButton-root': {
            '&.Mui-selected': {
              bgcolor: alpha(branding.primaryColor, 0.15),
              borderLeft: `3px solid ${branding.primaryColor}`,
            },
            '&:hover': {
              bgcolor: alpha(branding.primaryColor, 0.08),
            }
          }
        };
      case 'minimal':
        return {
          bgcolor: '#fafafa',
          '& .MuiListItemButton-root': {
            '&.Mui-selected': {
              bgcolor: '#f0f0f0',
              borderLeft: `3px solid ${branding.accentColor}`,
            },
            '&:hover': {
              bgcolor: '#f5f5f5',
            }
          }
        };
      default: // modern
        return {
          bgcolor: alpha(branding.primaryColor, 0.03),
          '& .MuiListItemButton-root': {
            '&.Mui-selected': {
              bgcolor: alpha(branding.primaryColor, 0.1),
              borderLeft: `3px solid ${branding.primaryColor}`,
            },
            '&:hover': {
              bgcolor: alpha(branding.primaryColor, 0.05),
            }
          }
        };
    }
  };

  const currentThread = getCurrentThread();
  const messages = currentThread?.messages || [];
  const totalCredits = currentThread?.totalCredits || 0;

  return (
    <Box 
      sx={{ height: '100vh', display: 'flex', flexDirection: 'column', position: 'relative' }}
      key={`chatbot-embed-${branding.layoutStyle}-${branding.bannerUrl ? 'with-banner' : 'no-banner'}`}
    >
      {/* Thread Drawer */}
      <Drawer
        variant="persistent"
        anchor="left"
        open={showThreadDrawer}
        sx={{
          width: showThreadDrawer ? 280 : 0,
          flexShrink: 0,
          position: 'absolute',
          height: '100%',
          '& .MuiDrawer-paper': {
            width: 280,
            position: 'absolute',
            height: '100%',
            pt: 8,
            ...getDrawerStyle()
          },
        }}
      >
        <Box sx={{ 
          p: 2, 
          borderBottom: '1px solid', 
          borderColor: 'divider',
          bgcolor: branding.layoutStyle === 'minimal' ? 'white' : alpha(branding.primaryColor, 0.05),
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Previous Conversations
            </Typography>
            <IconButton 
              size="small"
              onClick={() => setShowThreadDrawer(false)}
              sx={{ color: branding.primaryColor }}
            >
              <ChevronLeftIcon />
            </IconButton>
          </Box>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={startNewConversation}
            sx={{ 
              mb: 1,
              borderColor: branding.primaryColor,
              color: branding.primaryColor,
              '&:hover': {
                borderColor: branding.primaryColor,
                bgcolor: alpha(branding.primaryColor, 0.05),
              }
            }}
          >
            New Conversation
          </Button>
          <Typography variant="caption" color="textSecondary">
            {threads.length} conversations
          </Typography>
        </Box>
        <List sx={{ flex: 1, overflow: 'auto', py: 0 }}>
          {threads.map((thread) => (
            <ListItemButton
              key={thread.id}
              selected={thread.id === currentThreadId}
              onClick={() => selectThread(thread.id)}
              sx={{ pl: 2 }}
            >
              <ListItemText
                primary={thread.title}
                secondary={new Date(thread.timestamp).toLocaleString()}
                primaryTypographyProps={{ noWrap: true }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  size="small"
                  onClick={(e) => deleteThread(thread.id, e)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItemButton>
          ))}
        </List>
      </Drawer>

      {/* Header */}
      <Box sx={getHeaderStyle()}>
        <IconButton
          size="small"
          sx={{
            color: branding.layoutStyle === 'minimal' && !branding.bannerUrl ? 'text.primary' : 'white',
            bgcolor: branding.layoutStyle === 'minimal' && !branding.bannerUrl ? 'grey.100' : 'rgba(255,255,255,0.1)',
            '&:hover': {
              bgcolor: branding.layoutStyle === 'minimal' && !branding.bannerUrl ? 'grey.200' : 'rgba(255,255,255,0.2)',
            },
            ...(branding.layoutStyle === 'classic' && !branding.bannerUrl && {
              position: 'absolute',
              left: 10,
              top: '50%',
              transform: 'translateY(-50%)',
            })
          }}
          onClick={() => setShowThreadDrawer(!showThreadDrawer)}
        >
          {showThreadDrawer ? <ChevronLeftIcon /> : <MenuIcon />}
        </IconButton>
        {branding.logoUrl && !branding.bannerUrl && (
          <Box sx={getLogoStyle()} />
        )}
        <Box sx={branding.layoutStyle === 'classic' && !branding.bannerUrl ? { textAlign: 'center', width: '100%' } : { flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {overrideTitle || branding.name}
          </Typography>
        </Box>
      </Box>

      {/* Messages */}
      <Box sx={{
        flex: 1,
        overflowY: 'auto',
        p: 3,
        bgcolor: '#f8f9fa',
        ml: showThreadDrawer ? '280px' : 0,
        transition: 'margin 0.3s',
      }}>
        {messages.map((msg, idx) => (
          <Box
            key={idx}
            sx={{
              mb: 2,
              display: 'flex',
              justifyContent: msg.isUser ? 'flex-end' : 'flex-start'
            }}
          >
            <Box sx={{
              maxWidth: '70%',
              p: 2,
              borderRadius: '18px',
              background: msg.isUser 
                ? '#e3f2fd'
                : (branding.layoutStyle === 'minimal' ? '#f0f0f0' : branding.primaryColor),
              color: msg.isUser 
                ? '#1976d2' 
                : (branding.layoutStyle === 'minimal' ? 'text.primary' : 'white'),
              border: msg.isUser ? '1px solid #90caf9' : 'none',
              borderBottomLeftRadius: msg.isUser ? '18px' : '4px',
              borderBottomRightRadius: msg.isUser ? '4px' : '18px',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}>
              {/* File attachments */}
              {msg.attachments && msg.attachments.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  {msg.attachments.map((file, fileIdx) => (
                    <Box key={fileIdx} sx={{ mb: 0.5 }}>
                      {file.inputType === 'image' ? (
                        <Box
                          component="img"
                          src={file.dataUrl}
                          alt={file.name}
                          sx={{
                            maxWidth: '100%',
                            maxHeight: 300,
                            borderRadius: 2,
                            cursor: 'pointer',
                            '&:hover': { opacity: 0.9 }
                          }}
                          onClick={() => window.open(file.dataUrl, '_blank')}
                        />
                      ) : file.inputType === 'video' ? (
                        <Box
                          component="video"
                          controls
                          src={file.dataUrl}
                          sx={{
                            maxWidth: '100%',
                            maxHeight: 300,
                            borderRadius: 2
                          }}
                        />
                      ) : file.inputType === 'audio' ? (
                        <Box
                          component="audio"
                          controls
                          src={file.dataUrl}
                          sx={{ width: '100%' }}
                        />
                      ) : (
                        <Chip
                          icon={
                            file.inputType === 'pdf' ? <PdfIcon /> :
                            file.inputType === 'csv' ? <FileIcon /> :
                            <FileIcon />
                          }
                          label={`${file.name} (${(file.size / 1024).toFixed(1)} KB)`}
                          size="small"
                          sx={{ mb: 0.5 }}
                        />
                      )}
                    </Box>
                  ))}
                </Box>
              )}
              {msg.isUser ? (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {msg.text}
                </Typography>
              ) : (
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeSanitize]}
                  components={{
                    p: (props: any) => <Typography variant="body2" {...props} />,
                    li: (props: any) => <li {...props} />,
                    strong: (props: any) => <strong {...props} />,
                    em: (props: any) => <em {...props} />,
                    code: ({ inline, className, children, ...props }: any) =>
                      inline ? (
                        <code className={className} {...props}>
                          {children}
                        </code>
                      ) : (
                        <pre>
                          <code className={className} {...props}>
                            {children}
                          </code>
                        </pre>
                      ),
                  }}
                >
                  {msg.text}
                </ReactMarkdown>
              )}
            </Box>
          </Box>
        ))}
        {sending && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
            <Box sx={{
              p: 2,
              borderRadius: '18px',
              background: branding.layoutStyle === 'minimal' ? '#f0f0f0' : branding.primaryColor,
              display: 'flex',
              gap: 0.5
            }}>
              {[0, 1, 2].map(i => (
                <Box key={i} sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: branding.layoutStyle === 'minimal' ? 'grey.500' : 'rgba(255,255,255,0.7)',
                  animation: 'bounce 1.4s infinite ease-in-out',
                  animationDelay: `${-0.32 + i * 0.16}s`
                }} />
              ))}
            </Box>
          </Box>
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Input Area */}
      <Box sx={{
        p: 2,
        bgcolor: 'white',
        borderTop: '1px solid #e0e0e0',
        ml: showThreadDrawer ? '280px' : 0,
        transition: 'margin 0.3s',
      }}>
        {/* File Attachments Preview */}
        {attachedFiles.length > 0 && (
          <Box sx={{ mb: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {attachedFiles.map((file, idx) => (
              <Chip
                key={idx}
                icon={
                  file.inputType === 'image' ? <ImageIcon /> :
                  file.inputType === 'video' ? <VideoIcon /> :
                  file.inputType === 'audio' ? <AudioIcon /> :
                  file.inputType === 'pdf' ? <PdfIcon /> :
                  <FileIcon />
                }
                label={`${file.name} (${(file.size / 1024).toFixed(1)} KB)`}
                onDelete={() => removeAttachedFile(idx)}
                deleteIcon={<CloseIcon />}
                color="primary"
                variant="outlined"
                sx={{ maxWidth: 250 }}
              />
            ))}
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {allowAttachments && (
            <>
              <input
                type="file"
                multiple
                accept="image/*,video/*,audio/*,.pdf,.csv,.html,.md,.txt,.doc,.docx"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                id="chatbot-embed-file-input"
                disabled={attachedFiles.length >= MAX_FILES}
              />
              <label htmlFor="chatbot-embed-file-input" style={{ display: 'flex', alignItems: 'center' }}>
                <IconButton
                  component="span"
                  disabled={sending || attachedFiles.length >= MAX_FILES}
                  sx={{
                    color: branding.primaryColor,
                    '&:hover': {
                      bgcolor: alpha(branding.primaryColor, 0.1)
                    }
                  }}
                  title={`Attach files (${attachedFiles.length}/${MAX_FILES})`}
                >
                  <AttachFileIcon />
                </IconButton>
              </label>
            </>
          )}
          <TextField
            fullWidth
            size="medium"
            placeholder="Type your message..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !sending && (input.trim() || attachedFiles.length > 0)) {
                sendMessage();
              }
            }}
            disabled={sending}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '24px',
              }
            }}
          />
          <Button
            variant="contained"
            onClick={sendMessage}
            disabled={sending || (!input.trim() && attachedFiles.length === 0)}
            startIcon={<SendIcon />}
            sx={{
              background: branding.accentColor,
              borderRadius: '24px',
              px: 3,
              minHeight: '40px',
              '&:hover': {
                background: branding.accentColor,
                filter: 'brightness(0.85)',
              },
              '&.Mui-disabled': {
                background: '#e0e0e0',
                color: '#9e9e9e',
              },
            }}
          >
            Send
          </Button>
        </Box>
        {showCreditCounter && (
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
            Credits used: {totalCredits.toFixed(2)}
          </Typography>
        )}
      </Box>

      <style>{`
        @keyframes bounce {
          0%, 80%, 100% { transform: scale(0); }
          40% { transform: scale(1); }
        }
        
        /* Markdown styling */
        code {
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', 'Courier New', monospace;
          font-size: 0.9em;
          padding: 2px 6px;
          border-radius: 4px;
          background-color: rgba(0, 0, 0, 0.06);
        }
        
        pre {
          background-color: #f6f8fa;
          border-radius: 8px;
          padding: 16px;
          overflow-x: auto;
          margin: 8px 0;
          border: 1px solid #e1e4e8;
        }
        
        pre code {
          background-color: transparent;
          padding: 0;
          font-size: 0.85em;
          line-height: 1.5;
        }
        
        /* Markdown lists */
        ul, ol {
          padding-left: 20px;
          margin: 8px 0;
        }
        
        li {
          margin: 4px 0;
        }
        
        /* Markdown links */
        a {
          color: #0366d6;
          text-decoration: none;
        }
        
        a:hover {
          text-decoration: underline;
        }
        
        /* Markdown headings in bot messages */
        h1, h2, h3, h4, h5, h6 {
          margin: 12px 0 8px 0;
          font-weight: 600;
        }
        
        /* Markdown blockquotes */
        blockquote {
          border-left: 4px solid #dfe2e5;
          padding-left: 16px;
          margin: 8px 0;
          color: #6a737d;
        }
        
        /* Markdown tables */
        table {
          border-collapse: collapse;
          width: 100%;
          margin: 8px 0;
        }
        
        th, td {
          border: 1px solid #dfe2e5;
          padding: 8px 12px;
          text-align: left;
        }
        
        th {
          background-color: #f6f8fa;
          font-weight: 600;
        }
      `}</style>
    </Box>
  );
};

export default ChatbotEmbed;
